import { AssetCache } from './cache.js';
import { LiveViewer } from './live-controller.js';
import { LiveMapLoader } from './live-map-loader.js';
import { Status } from './live-codec.js';
import { MapRenderer } from './renderer.js';
import { logger } from './log.js';

const log = logger('Embed');
const canvas = document.getElementById('canvas'), status = document.getElementById('status');
const serverId = Number(document.getElementById('viewer').dataset.serverId);
const diagnostic = message => log.warn(message);
const cache = new AssetCache(diagnostic);
const labels = {
    [Status.Disconnected]: 'Disconnected · waiting for the server to reconnect.',
    [Status.AwaitingSnapshot]: 'Waiting for a full update…',
    [Status.Loading]: 'Loading map…',
    [Status.Live]: 'Live',
    [Status.Stale]: 'Updates delayed · waiting for synchronization.',
    [Status.Idle]: 'Waiting for live updates…',
    [Status.MapUnavailable]: 'Map unavailable · retrying shortly…',
    [Status.MapChanging]: 'Changing map…',
};
let renderer, viewer, loader, latest, loading = false, retry;
const show = text => { status.textContent = text; };
const refresh = () => { if (!loading && latest) show(labels[latest.status] ?? 'Connecting…'); };
const connect = () => { clearTimeout(retry); latest = null; loading = false; show('Connecting…'); viewer.selectServer(serverId); };
const recover = error => {
    if (error?.name === 'AbortError') return;
    diagnostic(error?.message ?? 'Map unavailable');
    show('Preview unavailable · retrying shortly…');
    clearTimeout(retry); retry = setTimeout(connect, 15000);
};
try {
    // Camera input stays local; the passive stream never reports it in-game.
    renderer = new MapRenderer(canvas, diagnostic);
    loader = new LiveMapLoader({ renderer, cache, onProgress: show, onDiagnostic: diagnostic, onFailure: recover });
    viewer = new LiveViewer({ renderer, passive: true,
        onStatus: message => {
            latest = message; refresh();
            if (message.status === Status.MapUnavailable) recover(new Error(message.diagnostic ?? 'Map unavailable'));
        },
        loadMap: async (map, context) => {
            loading = true;
            try { return await loader.load(map, context); }
            finally { if (!context.signal.aborted) { loading = false; refresh(); } }
        },
    });
    connect();
} catch (error) { diagnostic(error.message); show('Live preview unavailable in this browser.'); }

canvas.addEventListener('webglcontextlost', event => {
    event.preventDefault(); clearTimeout(retry); viewer?.disconnect(); loader?.cancel();
    show('Graphics context lost. Waiting for recovery…');
});
canvas.addEventListener('webglcontextrestored', () => { if (viewer) connect(); });
window.addEventListener('pagehide', () => {
    clearTimeout(retry); viewer?.dispose(); loader?.dispose(); renderer?.dispose();
});
window.addEventListener('pageshow', event => { if (event.persisted) location.reload(); });
